import React, { useState } from "react";
import useInventoryBin from "../../../utils/hooks/inventory/inventoryBin";
import BinInfo from "./BinInfo";
import InfoIcon from "../../common/FlashPCBTooltip/InfoIcon";
import FlashPCBTooltip from "../../common/FlashPCBTooltip";
import Modal from "../../common/Modal";
import ModalContentWithCloseButton from "../../common/ModalContentWithCloseButton";
import { FakeLink } from "../../../constants/Styles";
import { useCustomConfirm } from "../../../utils/hooks/alerts";
import LoadingIcon from "../../common/LoadingIcon";
import { InventoryBin } from "../parts/Bins/inventoryBinTypes";
import { useRemoveBoardStencilAssociation } from "../../../utils/hooks/commonly-stored/uploaded-board/updates/board";

import AddOrUpdateInventoryItem from "../parts/AddOrUpdateInventoryItem";
import {
  InventoryItem,
  InventoryItemType,
} from "../../../utils/hooks/inventory/useInventoryItems";
import { ItemLocationDetails, VerticalList } from "./BoardInventoryStyles";

export default function StencilInfo({
  stencil,
  boardBin,
  boardId,
  onUpdate,
}: {
  stencil: InventoryItem;
  boardBin: InventoryBin | null;
  boardId: string;
  onUpdate: () => void;
}) {
  const { inventoryBin, isLoading, cannotFindBin } = useInventoryBin(
    stencil.flashpcb_bin_barcode,
  );
  const { removeStencilFromBoard } = useRemoveBoardStencilAssociation();
  const [updateStencil, setUpdateStencil] = useState<boolean>(false);
  const customConfirm = useCustomConfirm();
  const closeModal = () => {
    setUpdateStencil(false);
  };

  const onSuccessfullyUpdatedStencil = () => {
    closeModal();
    onUpdate();
  };

  const handleDisassociateStencil = () => {
    customConfirm(
      "Are you sure you want to disassociate this stencil from the project?",
      () => {
        removeStencilFromBoard(boardId, { stencil_ids: [stencil.id] });
      },
    );
  };

  const stencilHeader =
    stencil.stencil_item_details &&
    `${stencil.stencil_item_details.stencil_type} stencil (${stencil.flashpcb_container_barcode}) `;
  const stencilActions = (
    <>
      <FakeLink onClick={() => setUpdateStencil(true)}>Update</FakeLink>
      <FakeLink onClick={handleDisassociateStencil}>
        Disassociate stencil from project
      </FakeLink>
    </>
  );

  if (stencil.flashpcb_bin_barcode !== null && isLoading) {
    return <LoadingIcon text={"Loading..."} />;
  }

  return (
    <>
      {inventoryBin &&
      boardBin &&
      inventoryBin.flashpcb_bin_barcode === boardBin.flashpcb_bin_barcode ? (
        `${stencilHeader} in board tote`
      ) : inventoryBin ? (
        <VerticalList>
          {stencilHeader}
          <FlashPCBTooltip
            content={
              <BinInfo
                binName={inventoryBin.bin_name}
                binLocation={inventoryBin.bin_location}
                binBarcode={inventoryBin.flashpcb_bin_barcode}
                binType={inventoryBin.bin_type}
                canAllocateFromBin={inventoryBin.can_allocate_from_this_bin}
                forTooltip
              />
            }
          >
            <ItemLocationDetails>
              <>Stencil location </>
              <InfoIcon />
            </ItemLocationDetails>
          </FlashPCBTooltip>
        </VerticalList>
      ) : (
        `${stencilHeader} ${cannotFindBin || stencil.flashpcb_bin_barcode === null ? "not associated with a bin" : ""}`
      )}
      {stencilActions}
      {updateStencil && (
        <Modal isOpen={updateStencil} onRequestClose={closeModal}>
          <ModalContentWithCloseButton closeModal={closeModal}>
            <AddOrUpdateInventoryItem
              itemType={InventoryItemType.STENCIL}
              itemToUpdate={stencil}
              onCancel={closeModal}
              onSuccess={onSuccessfullyUpdatedStencil}
              boardId={boardId}
            />
          </ModalContentWithCloseButton>
        </Modal>
      )}
    </>
  );
}
